import React from "react";

/**
 * Minimal markdown-ish inline renderer for the controlled article corpus.
 * Supports **bold** and [text](url). Author-controlled content (no XSS risk).
 */
const renderInline = (text) => {
  if (!text) return null;
  const parts = [];
  let remaining = text;
  let key = 0;
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/;
  const boldRe = /\*\*([^*]+)\*\*/;

  while (remaining.length) {
    const linkMatch = remaining.match(linkRe);
    const boldMatch = remaining.match(boldRe);
    let nextIdx = -1;
    let kind = null;
    if (linkMatch && (nextIdx === -1 || linkMatch.index < nextIdx)) {
      nextIdx = linkMatch.index; kind = "link";
    }
    if (boldMatch && (nextIdx === -1 || boldMatch.index < nextIdx)) {
      nextIdx = boldMatch.index; kind = "bold";
    }
    if (nextIdx === -1) {
      parts.push(<React.Fragment key={key++}>{remaining}</React.Fragment>);
      break;
    }
    if (nextIdx > 0) parts.push(<React.Fragment key={key++}>{remaining.slice(0, nextIdx)}</React.Fragment>);
    if (kind === "link") {
      const [, label, href] = linkMatch;
      const isInternal = href.startsWith("/") || href.startsWith("#");
      parts.push(
        <a
          key={key++}
          href={href}
          className="text-[#007AFF] hover:underline font-medium"
          {...(isInternal ? {} : { target: "_blank", rel: "noopener noreferrer" })}
        >
          {label}
        </a>
      );
      remaining = remaining.slice(nextIdx + linkMatch[0].length);
    } else if (kind === "bold") {
      const [, content] = boldMatch;
      parts.push(<strong key={key++} className="text-white font-semibold">{content}</strong>);
      remaining = remaining.slice(nextIdx + boldMatch[0].length);
    }
  }
  return parts;
};

export const ArticleRenderer = ({ content }) => {
  return (
    <div className="article-prose font-body text-white/80">
      {content.map((block, i) => {
        switch (block.kind) {
          case "h2":
            return (
              <h2
                key={i}
                className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-white mt-14 mb-5"
              >
                {block.text}
              </h2>
            );
          case "h3":
            return (
              <h3
                key={i}
                className="font-display text-xl md:text-2xl font-semibold text-white mt-10 mb-4"
              >
                {block.text}
              </h3>
            );
          case "p":
            return (
              <p key={i} className="text-base md:text-lg leading-relaxed mb-5">
                {renderInline(block.text)}
              </p>
            );
          case "ul":
            return (
              <ul key={i} className="space-y-2.5 mb-6 ml-1">
                {block.items.map((it, j) => (
                  <li key={j} className="flex gap-3 text-base md:text-lg leading-relaxed">
                    <span className="mt-2 inline-block h-1.5 w-1.5 rounded-full bg-[#007AFF] flex-shrink-0" />
                    <span>{renderInline(it)}</span>
                  </li>
                ))}
              </ul>
            );
          case "ol":
            return (
              <ol key={i} className="space-y-3 mb-6 ml-1 list-none">
                {block.items.map((it, j) => (
                  <li key={j} className="flex gap-4 text-base md:text-lg leading-relaxed">
                    <span className="font-display text-[#007AFF] font-semibold flex-shrink-0 w-6">
                      {j + 1}.
                    </span>
                    <span>{renderInline(it)}</span>
                  </li>
                ))}
              </ol>
            );
          case "quote":
            return (
              <blockquote
                key={i}
                className="my-10 border-l-2 border-[#007AFF] pl-6 py-2 italic font-display text-xl md:text-2xl text-white/90"
              >
                « {renderInline(block.text)} »
              </blockquote>
            );
          default:
            return null;
        }
      })}
    </div>
  );
};

export default ArticleRenderer;
